import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface AdminNotifications {
  prayer: number;
  contact: number;
  donations: number;
  total: number;
}

const EMPTY: AdminNotifications = { prayer: 0, contact: 0, donations: 0, total: 0 };

/**
 * Counts brand-new (unhandled) items across the inbox tables so the admin
 * dashboard can show notification bells and badges.
 */
export function useAdminNotifications(pollMs = 60000) {
  const [counts, setCounts] = useState<AdminNotifications>(EMPTY);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [prayerRes, contactRes, donationRes] = await Promise.all([
        supabase.from('prayer_requests').select('*', { count: 'exact', head: true }).eq('status', 'new'),
        supabase.from('contact_submissions').select('*', { count: 'exact', head: true }).eq('status', 'new'),
        supabase.from('donations').select('*', { count: 'exact', head: true }).eq('status', 'new'),
      ]);

      const prayer = prayerRes.error ? 0 : prayerRes.count ?? 0;
      const contact = contactRes.error ? 0 : contactRes.count ?? 0;
      const donations = donationRes.error ? 0 : donationRes.count ?? 0;

      setCounts({ prayer, contact, donations, total: prayer + contact + donations });
    } catch {
      setCounts(EMPTY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, pollMs);
    return () => clearInterval(timer);
  }, [load, pollMs]);

  return { counts, loading, reload: load };
}